import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { scrollPageToTop } from "../../utils/scrollToTop";

export function usePagination(items = [], resetKeys = [], initialPageSize = 10) {
  const [page, setPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const firstScrollRender = useRef(true);
  const resetSignature = useMemo(() => resetKeys.map((key) => String(key ?? "")).join("|"), [resetKeys]);

  const setPage = useCallback((value) => {
    setPageState((currentPage) => {
      const nextPage = typeof value === "function" ? value(currentPage) : Number(value);
      return Math.max(1, nextPage || 1);
    });
  }, []);

  const setPageSize = useCallback(
    (value) => {
      const nextSize = Number(value) || initialPageSize;

      setPageSizeState(Math.max(1, nextSize));
      setPageState(1);
    },
    [initialPageSize]
  );

  useEffect(() => {
    setPageState(1);
  }, [resetSignature]);

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const currentPage = Math.min(page, totalPages);

  useEffect(() => {
    setPageState((current) => Math.min(current, totalPages));
  }, [totalPages]);

  useEffect(() => {
    if (firstScrollRender.current) {
      firstScrollRender.current = false;
      return;
    }

    window.requestAnimationFrame(() => scrollPageToTop({ behavior: "smooth" }));
  }, [currentPage, pageSize]);

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [currentPage, items, pageSize]);

  return {
    page: currentPage,
    pageSize,
    paginatedItems,
    setPage,
    setPageSize,
    totalItems: items.length,
    totalPages,
  };
}
